import { useStore } from '@nanostores/react';
import { Avatar, Flex, Heading, IconButton, Popover, Select, Separator, Text, Tooltip } from '@radix-ui/themes';
import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { TbCopy, TbDeviceDesktop, TbLogout, TbMoon, TbSun, TbUsersGroup } from 'react-icons/tb';
import { Avatar as AvatarComponent } from '~/components/Avatar/Avatar';
import { useSession } from '~/features/auth/context/Session';
import { useAlphabetisedOrganisations } from '~/hooks/useAlphabetisedOrganisations';
import { useOrganisation } from '~/hooks/useOrganisation';
import { interfaceStore } from '~/stores/interface';
import { projectStore } from '~/stores/project';
import { capitalise } from '~/utils/capitalise';
import { copyToClipboard } from '~/utils/copyToClipboard';
import Styles from './account-dropdown.module.scss';

export function AccountDropdown({
  isPopoverOpen,
  setIsPopoverOpen,
}: {
  isPopoverOpen: boolean;
  setIsPopoverOpen: (isPopoverOpen: boolean) => void;
}) {
  const { organisation, organisations, onUpdateActiveOrganisation } = useOrganisation();
  const { session, handleLogout } = useSession();
  const navigate = useNavigate();
  const themePreference = useStore(interfaceStore.themePreference);
  const project = useStore(projectStore.project);

  const orgOptions = useAlphabetisedOrganisations({ orgs: organisations, active: organisation });

  const activeOrg = useMemo(
    () => ({
      label: organisation?.displayName,
      value: organisation?.id,
    }),
    [organisation]
  );

  const handleUpdateActiveOrganisation = (value: string) => {
    setIsPopoverOpen(false);
    onUpdateActiveOrganisation(value);
  };

  const onNavigateToAccount = (route: string, search?: Record<string, unknown>) => {
    setIsPopoverOpen(false);
    navigate({ to: '/account', search: { ...search } });
  };

  const handleCopyOrganisationId = () => {
    copyToClipboard(organisation?.id, 'Organisation ID copied');
  };

  const initial = useMemo(() => {
    if (session?.identity?.traits?.given_name && session?.identity?.traits?.family_name) {
      return `${session?.identity?.traits?.given_name?.[0]}`.toUpperCase();
    }
    return session?.identity?.traits?.email?.[0];
  }, [session]);

  const picture = session?.identity?.traits?.picture || undefined;

  return (
    <Popover.Root open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <Popover.Trigger>
        <IconButton size="2" variant="soft" radius="large">
          <Avatar radius="medium" size="2" fallback={capitalise(initial)} src={picture} />
        </IconButton>
      </Popover.Trigger>
      <Popover.Content
        align="end"
        autoFocus={false}
        sideOffset={12}
        style={{ backgroundColor: 'var(--gray-2)' }}
        minWidth="25rem"
      >
        <Flex direction="column" gap="2">
          <AvatarComponent handleNavigationToAccount={onNavigateToAccount} />
          <Separator size="4" />
          <Flex direction="column" gap="2" py="1">
            <Flex align="center" gap="2" justify="between" position="relative">
              <Flex align="center" gap="2">
                <TbUsersGroup />
                <Heading size="2" weight="medium">
                  Organisation
                </Heading>
              </Flex>

              <Tooltip content="Copy Organisation ID" side="left">
                <Flex
                  role="button"
                  tabIndex={0}
                  onClick={handleCopyOrganisationId}
                  onKeyUp={(e) => {
                    if (e.key === 'Enter') {
                      handleCopyOrganisationId();
                    }
                  }}
                  className={Styles.AccountDropdownCopyOrganisationId}
                >
                  <TbCopy />
                </Flex>
              </Tooltip>
            </Flex>
            <Select.Root
              defaultValue={activeOrg.value}
              onValueChange={(value) => handleUpdateActiveOrganisation(value)}
            >
              <Select.Trigger placeholder="Select Organisation" />
              <Select.Content position="popper" align="end" style={{ maxHeight: '20rem', overflow: 'auto' }}>
                {orgOptions?.map((org) => (
                  <Select.Item key={org.value} value={org.value}>
                    {org.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Flex>

          <Flex width="100%" direction="column" gap="2">
            <Flex
              role="button"
              tabIndex={0}
              onClick={() =>
                onNavigateToAccount(
                  '/account',
                  project?.id ? { backToProjectId: project.id, content: 'billing' } : { content: 'billing' }
                )
              }
              className={Styles.AccountDropdownLink}
            >
              <Flex align="center" gap="2">
                <Text size="2" weight="medium">
                  Organisation Settings
                </Text>
              </Flex>
            </Flex>
            <Flex
              role="button"
              tabIndex={0}
              onClick={() =>
                onNavigateToAccount(
                  '/account',
                  project?.id ? { backToProjectId: project.id, content: 'members' } : { content: 'members' }
                )
              }
              className={Styles.AccountDropdownLink}
            >
              <Flex align="center" gap="2">
                <Text size="2" weight="medium">
                  Invite Members
                </Text>
              </Flex>
            </Flex>
          </Flex>
          <Separator size="4" />

          <Flex width="100%">
            <Flex align="center" gap="3" width="100%">
              <Text size="2" weight="medium">
                Appearance
              </Text>
              <Tooltip content="Light">
                <Flex
                  role="button"
                  align="center"
                  gap="2"
                  tabIndex={0}
                  className={Styles.AccountDropdownAppearanceMode}
                  onClick={() => interfaceStore.setAppearanceMode('light')}
                  onKeyUp={(e) => {
                    if (e.key === 'Enter') {
                      interfaceStore.setAppearanceMode('light');
                    }
                  }}
                >
                  <TbSun color={themePreference === 'light' ? 'var(--gray-12)' : 'var(--gray-10)'} />
                </Flex>
              </Tooltip>
              <Separator orientation="vertical" size="4" />
              <Tooltip content="Dark">
                <Flex
                  role="button"
                  align="center"
                  gap="2"
                  className={Styles.AccountDropdownAppearanceMode}
                  tabIndex={0}
                  onClick={() => interfaceStore.setAppearanceMode('dark')}
                  onKeyUp={(e) => {
                    if (e.key === 'Enter') {
                      interfaceStore.setAppearanceMode('dark');
                    }
                  }}
                >
                  <TbMoon color={themePreference === 'dark' ? 'var(--gray-12)' : 'var(--gray-10)'} />
                </Flex>
              </Tooltip>
              <Separator orientation="vertical" size="4" />
              <Tooltip content="System">
                <Flex
                  role="button"
                  align="center"
                  gap="2"
                  className={Styles.AccountDropdownAppearanceMode}
                  tabIndex={0}
                  onClick={() => interfaceStore.setAppearanceMode('system')}
                  onKeyUp={(e) => {
                    if (e.key === 'Enter') {
                      interfaceStore.setAppearanceMode('system');
                    }
                  }}
                >
                  <TbDeviceDesktop color={themePreference === 'system' ? 'var(--gray-12)' : 'var(--gray-10)'} />
                </Flex>
              </Tooltip>
            </Flex>
          </Flex>

          <a
            href="https://docs.versori.com/v2/introduction"
            target="_blank"
            rel="noopener noreferrer"
            className={Styles.AccountDropdownLink}
          >
            <Flex align="center" gap="2">
              <Text size="2" weight="medium" style={{ color: 'inherit' }}>
                Documentation
              </Text>
            </Flex>
          </a>
          <Separator size="4" />

          <Flex
            role="button"
            tabIndex={0}
            onClick={handleLogout}
            className={Styles.AccountDropdownLogout}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                handleLogout();
              }
            }}
          >
            <Flex align="center" gap="2">
              <TbLogout color="var(--red-9)" />
              <Text size="2" weight="medium" style={{ color: 'inherit' }}>
                Log out
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </Popover.Content>
    </Popover.Root>
  );
}
